import React, { useEffect, useState } from "react";
import { useCallback } from "react";
import { useNavigate } from 'react-router-dom';
import AuthData from "../common/AuthData";
import Fetch from "../services/fetchService";
import RelativeSvgLoader from "../common/components/RelativeSvgLoader";
import Product from "../common/components/Product";
import Delete from '../services/deleteService'; // Import the Delete service
import YesNoPopup from '../common/components/YesNoPopup';
import { PRODUCTS_API } from '../common/ResourcePaths'; // Import the constants
import '../styles/ProductList.css'; // Import the CSS file
import Feedback from '../common/components/Feedback';
import NewProduct from './NewProduct';

function ProductList() {
    const [isLoading, setIsLoading] = useState(true);
    const [data, setData] = useState(null);
    const [pageNumber, setPageNumber] = useState(1); 
    const [totalPages,setTotalPages] = useState(0);
    const [showPopup, setShowPopup] = useState(false);
    const [editedId, setEditedId] = useState(null); // State to track the edited item ID
    const [searchText, setSearchText] = useState(''); // State for search input
    const [selectedCategory, setSelectedCategory] = useState(null); // State for category filter
    const [categories, setCategories] = useState([]); // State for available categories
    const [showAddProduct, setShowAddProduct] = useState(false); // State to toggle add product view

    const pageSize = 8;
    const navigate = useNavigate();
    
    const fetchData = useCallback(async (currentPage, searchText = '', category = '', isStartup = false) => {
        setIsLoading(true);
        try {
            const authData = AuthData.getAuthData();
           const url = `${authData.appFunctionUrl}${PRODUCTS_API}?code=${authData.azureFunctionKey}&searchBy=${encodeURIComponent(searchText)}&category=${encodeURIComponent(category)}&pageSize=${pageSize}&pageNumber=${currentPage}&draftInclude=true&timestamp=${new Date().getTime()}`;
            const response = await Fetch(url, authData.clientId);
            setData(response);
            // Ensure totalPages has a minimum value of 1
            setTotalPages(Math.max(1, Math.ceil(response.totalItems / pageSize)));
            
            // Extract categories on startup
            if (isStartup && response.categories) {
                setCategories(response.categories);
            }
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setIsLoading(false);
        }
    }, [pageSize]);

    useEffect(() => {
        fetchData(pageNumber, '', selectedCategory || '', pageNumber === 1);
    }, [fetchData, pageNumber, selectedCategory]);

    const handleEdit = (productId) => {
        navigate(`/items/edit/${productId}`);
    };

    const handleDelete = (id) => {
        setShowPopup(true);
        setEditedId(id); // Store the ID of the item to be deleted
    }

    const handlePaginationAfterDelete = (currentItemsOnPage) => {
        // If this was the last item on the page and we're not on page 1, go to previous page
        if (currentItemsOnPage === 1 && pageNumber > 1) {
            setPageNumber(prev => prev - 1);
        } else {
            // Otherwise, refresh the current page
            fetchData(pageNumber);
        }
    };

    const remove = async (itemId) => {
        try {
            setIsLoading(true); // Set loading state while deleting
            
            // Check how many items are currently on this page
            const currentItemsOnPage = data?.items?.length || 0;
            
            const authData = AuthData.getAuthData();
            const url = `${authData.appFunctionUrl}${PRODUCTS_API}/${itemId}?code=${authData.azureFunctionKey}`;
            await Delete(url, authData.clientId); 
            
            handlePaginationAfterDelete(currentItemsOnPage);
        } catch (error) {
            console.error("Error deleting item:", error);
            fetchData(pageNumber); // Refresh on error to ensure consistent state
        } finally {
            setIsLoading(false); // Reset loading state after deletion
        }
    };

    const handleNextPage = () => {
        if (pageNumber < totalPages) {
            setPageNumber(prev => prev + 1);
        }
    };

    const handlePreviousPage = () => {
        if (pageNumber > 1) {
            setPageNumber(prev => prev - 1);
        }
    };
    
    const handleYes = async () => {
        setShowPopup(false);
        await remove(editedId); // Call the remove function with the stored ID
    };

    const handleNo = () => {
        setShowPopup(false);
    };

    // Search functionality
    const onSearch = () => {
        if (searchText) {
            setPageNumber(1);
            fetchData(1, searchText, selectedCategory || '');
        }
    };

    const clearSearch = () => {
        setSearchText('');
        setPageNumber(1);
        fetchData(1, '', selectedCategory || '');
    };

    // Category filter functionality
    const filterByCategory = (category) => {
        setSelectedCategory(category);
        setPageNumber(1);
        fetchData(1, searchText, category);
    };

    const clearSelection = () => {
        if (selectedCategory) {
            setSelectedCategory(null);
            setPageNumber(1);
            fetchData(1, searchText, '');
        }
    };

    // Handle Enter key press in search box
    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            onSearch();
        }
    };

    // If showing add product form, render NewProduct component with return button
    if (showAddProduct) {
        return (
            <div className="container" style={{ position: 'relative' }}>
                <button 
                    className="return-to-products-button" 
                    onClick={() => {
                        setShowAddProduct(false);
                        fetchData(pageNumber, searchText, selectedCategory || '');
                    }}
                    title="Return to Products"
                >
                    ← Back
                </button>
                <NewProduct onSuccess={() => {
                    setShowAddProduct(false);
                    fetchData(1, '', '');
                    setSearchText('');
                    setSelectedCategory(null);
                    setPageNumber(1);
                }} />
            </div>
        );
    }

    return (
        <div className="container" style={{ position: 'relative' }}>
                {isLoading && <RelativeSvgLoader />}
                <div className="panel" style={{ opacity: isLoading ? 0.3 : 1 }}>
                    <div className="search-panel">
                        <input 
                            type="text" 
                            className="search-box" 
                            placeholder="Search products..." 
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            onKeyUp={handleKeyPress}
                        />
                        {searchText && (
                            <button className="search-button" onClick={clearSearch}>
                                X
                            </button>
                        )}
                    <button className="search-button" onClick={onSearch}>
                        Search
                    </button>
                    <button className="add-product-button" onClick={() => setShowAddProduct(true)}>
                        Add
                    </button>
                </div>

                {/* Category Buttons */}
                {categories.length > 0 && (
                    <div className="category-buttons">
                        {categories.map((category) => (
                            <button
                                key={category}
                                className={`category-button ${category === selectedCategory ? 'selected' : ''}`}
                                onClick={() => filterByCategory(category)}
                            >
                                {category}
                            </button>
                        ))}
                        <button className="clear-category-button" onClick={clearSelection}>
                            X
                        </button>
                    </div>
                )}

                {/* Display Items as Tiles (4 items per row) */}
                <div className="product-list" style={{ minHeight: '400px' }}>
                    {data?.items?.map((item) => (
                        <Product
                            key={item.id}
                            item={item}
                            handleEdit={handleEdit}
                            handleDelete={(id) => handleDelete(id)}
                        />
                    ))}
                </div>

                {/* Pagination */}
                <div style={{ textAlign: "center", marginTop: "20px" }}>
                    <button
                        className="pagination-button"
                        onClick={handlePreviousPage}
                        disabled={pageNumber === 1}
                    >
                        Previous
                    </button>
                    <span style={{ margin: "0 15px", fontSize: "16px" }}>
                        Page {pageNumber} of {totalPages}
                    </span>
                    <button
                        className="pagination-button"
                        onClick={handleNextPage}
                        disabled={pageNumber === totalPages}
                    >
                        Next
                    </button>
                </div>
            </div>
            
            {showPopup && (
                <YesNoPopup
                    message="Are you sure you want to delete this item?"
                    handleYes={handleYes}
                    handleNo={handleNo} />
            )}
            
            {/* Feedback system available on items page */}
            <Feedback />
        </div>
    );
}
export default ProductList;